/**
 * Teoria das cores aplicada ao miolo da agenda.
 *
 * Ideia central: o usuário escolhe UMA cor. A partir dela derivamos:
 *  1. um esquema de harmonia (análogo, complementar, tríade...) para a cor de destaque;
 *  2. neutros *tingidos* com o matiz da cor principal (a técnica que faz o layout
 *     parecer desenhado por um designer em vez de "solto");
 *  3. cores de linha/borda coerentes com o conjunto;
 *  4. contraste WCAG garantido, porque a agenda vai para o papel.
 */
import { clarear, comAlfa, escurecer, garantirContraste, hexParaHsl, hslParaHex, limitar, misturar, normalizarHex, razaoContraste, saturar, } from './convert.js';
/** Deslocamento de matiz (em graus) para a cor de destaque de cada esquema. */
export const DESLOCAMENTO_DESTAQUE = {
    MONOCROMATICO: 0,
    ANALOGO: 32,
    COMPLEMENTAR: 180,
    COMPLEMENTAR_DIVIDIDO: 152,
    TRIADE: 120,
    TETRADE: 90,
};
export const EXPLICACAO_ESQUEMA = {
    MONOCROMATICO: 'Um único matiz em variações de luz. Resultado minimalista e muito calmo — excelente para agendas de rotina densa.',
    ANALOGO: 'Matizes vizinhos no círculo cromático (cerca de 32°). Cria continuidade visual e sensação de serenidade.',
    COMPLEMENTAR: 'Matizes opostos (180°). Contraste máximo: o destaque "salta" da página e guia o olho para o que importa.',
    COMPLEMENTAR_DIVIDIDO: 'Uma cor e as duas vizinhas da sua oposta (152° e 208°). Contraste forte, porém mais equilibrado que o complementar.',
    TRIADE: 'Três matizes a 120° entre si. Paleta viva e lúdica, ótima para agendas criativas e planejamento de projetos.',
    TETRADE: 'Dois pares complementares (90°). A paleta mais rica; use com áreas grandes, como capas e divisórias.',
};
/** Escolhe o esquema quando o usuário deixa em "AUTO". */
export function resolverEsquema(cores) {
    if (cores.esquema !== 'AUTO')
        return cores.esquema;
    const { s } = hexParaHsl(cores.corPrincipal);
    // Cores suaves pedem harmonia discreta; cores saturadas suportam contraste maior.
    if (s < 30)
        return 'ANALOGO';
    if (s < 65)
        return 'COMPLEMENTAR_DIVIDIDO';
    return 'TRIADE';
}
export function explicarEscolhaAutomatica(cores, aplicado) {
    if (cores.esquema !== 'AUTO')
        return EXPLICACAO_ESQUEMA[aplicado];
    const { s } = hexParaHsl(cores.corPrincipal);
    const motivo = s < 30
        ? 'sua cor é pouco saturada, então harmonia discreta preserva a elegância'
        : s < 65
            ? 'sua cor tem saturação média, então o complementar dividido dá contraste sem gritar'
            : 'sua cor é bem saturada e aceita uma tríade vibrante';
    return `Escolhemos automaticamente porque ${motivo}. ${EXPLICACAO_ESQUEMA[aplicado]}`;
}
/** Gera o matiz de destaque derivado do esquema. */
export function matizDeDestaque(corPrincipal, esquema) {
    const base = hexParaHsl(corPrincipal);
    if (esquema === 'MONOCROMATICO') {
        return hslParaHex({
            h: base.h,
            s: limitar(base.s * 1.08, 22, 92),
            l: limitar(base.l - 17, 16, 58),
        });
    }
    return hslParaHex({
        h: (base.h + DESLOCAMENTO_DESTAQUE[esquema]) % 360,
        s: limitar(Math.max(base.s, 34), 28, 88),
        l: limitar(base.l, 24, 56),
    });
}
/** Todas as cores do esquema, útil para capas e degradês. */
export function matizesDoEsquema(corPrincipal, esquema) {
    const base = hexParaHsl(corPrincipal);
    const graus = {
        MONOCROMATICO: [0],
        ANALOGO: [0, 32, -32],
        COMPLEMENTAR: [0, 180],
        COMPLEMENTAR_DIVIDIDO: [0, 152, 208],
        TRIADE: [0, 120, 240],
        TETRADE: [0, 90, 180, 270],
    };
    return graus[esquema].map((g) => hslParaHex({
        h: (base.h + g + 360) % 360,
        s: limitar(Math.max(base.s, 30), 26, 88),
        l: limitar(base.l, 26, 60),
    }));
}
export function construirPaleta(cores) {
    const principal = normalizarHex(cores.corPrincipal);
    const base = hexParaHsl(principal);
    const esquema = resolverEsquema(cores);
    const t = limitar(cores.intensidadeFundo, 0, 1);
    const destaque = cores.corDestaque ? normalizarHex(cores.corDestaque) : matizDeDestaque(principal, esquema);
    const destaqueHsl = hexParaHsl(destaque);
    /** Saturação dos neutros: proporcional à cor principal, com teto baixo. */
    const satNeutra = (fator) => limitar(base.s * fator * (0.45 + t), 0, 15);
    /** Luminosidade dos neutros: interpola entre branco puro e levemente tingido. */
    const luzNeutra = (de, para) => de + (para - de) * t;
    const fundo = hslParaHex({ h: base.h, s: satNeutra(0.24), l: luzNeutra(99.4, 95.8) });
    const superficie = hslParaHex({ h: base.h, s: satNeutra(0.32), l: luzNeutra(97.6, 93.4) });
    const fundoAlt = hslParaHex({ h: base.h, s: satNeutra(0.38), l: luzNeutra(95.4, 90.2) });
    const borda = hslParaHex({ h: base.h, s: satNeutra(0.52), l: luzNeutra(90.4, 79.5) });
    const linha = hslParaHex({ h: base.h, s: satNeutra(0.44), l: luzNeutra(87.5, 77.5) });
    const alvoContraste = cores.contrasteAlto ? 7 : 4.5;
    let texto = hslParaHex({ h: base.h, s: limitar(base.s * 0.28, 3, 20), l: 15 });
    let textoSuave = hslParaHex({ h: base.h, s: limitar(base.s * 0.2, 3, 16), l: 40 });
    if (cores.corTexto) {
        texto = normalizarHex(cores.corTexto);
        textoSuave = misturar(texto, fundo, 0.42);
    }
    // O papel não perdoa contraste baixo: garantimos o mínimo sempre.
    texto = garantirContraste(texto, fundo, alvoContraste);
    textoSuave = garantirContraste(textoSuave, fundo, Math.min(alvoContraste, 4.5));
    const principalEscuro = escurecer(principal, limitar(base.l * 0.35, 8, 20));
    const principalClaro = clarear(saturar(principal, -6), limitar(100 - base.l, 18, 32));
    const destaqueSuave = hslParaHex({
        h: destaqueHsl.h,
        s: limitar(destaqueHsl.s * 0.5, 8, 46),
        l: 92,
    });
    const destaqueTexto = garantirContraste(hslParaHex({ h: destaqueHsl.h, s: limitar(destaqueHsl.s * 1.05, 28, 78), l: 34 }), fundo, alvoContraste);
    const inverso = hslParaHex({ h: base.h, s: 12, l: 99.2 });
    return {
        principal,
        principalEscuro,
        principalClaro,
        destaque,
        destaqueSuave,
        destaqueTexto,
        fundo,
        fundoAlt,
        superficie,
        texto,
        textoSuave,
        linha,
        borda,
        inverso,
        contrasteTexto: razaoContraste(texto, fundo),
        contrastePrincipal: razaoContraste(principal, inverso),
        esquemaAplicado: esquema,
        harmoniaExplicacao: explicarEscolhaAutomatica(cores, esquema),
    };
}
/** Estilos prontos de "fundo" para as seções, derivados da paleta. */
export function fundoDeSecao(paleta, tipo) {
    switch (tipo) {
        case 'SUAVE':
            return paleta.fundoAlt;
        case 'PRINCIPAL':
            return paleta.principal;
        case 'DESTAQUE':
            return paleta.destaque;
        case 'DESTAQUE_SUAVE':
            return paleta.destaqueSuave;
        case 'INVERSO':
            return paleta.principalEscuro;
        default:
            return 'transparent';
    }
}
/** Cor de texto legível sobre um fundo qualquer. */
export function textoLegivelSobre(paleta, fundo) {
    return razaoContraste(paleta.inverso, fundo) >= 4.5 ? paleta.inverso : paleta.texto;
}
export { comAlfa, misturar };
/* ------------------------------------------------------------------ */
/* Sugestões                                                           */
/* ------------------------------------------------------------------ */
/** PRNG determinístico simples (mulberry32) para sugestões reprodutíveis. */
function prng(semente) {
    let h = 1779033703 ^ semente.length;
    for (let i = 0; i < semente.length; i += 1) {
        h = Math.imul(h ^ semente.charCodeAt(i), 3432918353);
        h = (h << 13) | (h >>> 19);
    }
    let a = h >>> 0;
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/**
 * Sugere uma cor principal coerente com o "clima" desejado.
 * Usa faixas de matiz conhecidas em design editorial (quentes/frias/neutras).
 */
export function sugerirCorPrincipal(clima) {
    const faixas = {
        quente: [
            [8, 22],
            [28, 44],
            [0, 6],
        ],
        fria: [
            [198, 220],
            [228, 248],
            [168, 186],
        ],
        natural: [
            [88, 128],
            [60, 86],
            [150, 166],
        ],
        neutra: [
            [210, 240],
            [20, 40],
            [250, 270],
        ],
    };
    const opcoes = faixas[clima] ?? faixas.natural;
    const rnd = prng(`${clima}-${new Date().getFullYear()}`);
    const faixa = opcoes[Math.floor(rnd() * opcoes.length)];
    const h = Math.round(faixa[0] + rnd() * (faixa[1] - faixa[0]));
    return hslParaHex({ h, s: Math.round(38 + rnd() * 30), l: Math.round(34 + rnd() * 14) });
}
export const CLIMAS = [
    { id: 'quente', nome: 'Quente', descricao: 'Âmbar, terracota e vermelhos — energia e acolhimento.' },
    { id: 'fria', nome: 'Fria', descricao: 'Azuis e verdes-água — concentração e serenidade.' },
    { id: 'natural', nome: 'Natural', descricao: 'Verdes e olivas — equilíbrio e organicidade.' },
    { id: 'neutra', nome: 'Neutra', descricao: 'Cinzas, rosés e lavandas — elegância discreta.' },
];
//# sourceMappingURL=theory.js.map